import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

const TOOLTIP_CSS = `
.inv-tip-mark{
  position:relative;display:inline-flex;align-items:center;justify-content:center;
  vertical-align:middle;margin-left:4px;flex-shrink:0;
}
.inv-tip-trigger{
  width:14px;height:14px;border-radius:50%;
  border:1px solid rgba(120,130,140,.45);
  background:#fff;color:rgba(72,82,92,.95);
  font-size:9px;font-weight:700;line-height:1;
  display:inline-flex;align-items:center;justify-content:center;
  cursor:help;padding:0;font-family:ui-sans-serif,system-ui,sans-serif;
  box-shadow:0 1px 2px rgba(16,24,20,.06);
}
.inv-tip-bubble{
  position:fixed;z-index:10050;
  background:#fff;color:#2e3a35;
  font-size:11px;font-weight:500;font-family:ui-sans-serif,system-ui,sans-serif;
  line-height:1.45;padding:10px 12px;border-radius:10px;
  white-space:pre-line;max-width:min(280px,calc(100vw - 24px));min-width:140px;text-align:left;
  box-shadow:0 6px 24px rgba(16,24,20,.18);
  border:1px solid rgba(30,40,34,.1);
  pointer-events:none;
}
.inv-tip-bubble.inv-tip-bubble--dark{
  background:#1f2a24;color:#f0f4f2;
  border-color:rgba(148,163,184,.2);
}
@media(max-width:820px){
  .inv-tip-mark.inv-tip-open .inv-tip-trigger{border-color:rgba(181,154,91,.6);}
}
`;

function useInvelpsTooltipStyles() {
  useEffect(() => {
    if (document.getElementById("inv-tooltip-css")) return;
    const s = document.createElement("style");
    s.id = "inv-tooltip-css";
    s.textContent = TOOLTIP_CSS;
    document.head.appendChild(s);
  }, []);
}

/** Tooltip (? ) — bulle en portal (non coupée par overflow des cartes). */
export default function InvelpsTipMark({ title, down = false, dark = false }) {
  useInvelpsTooltipStyles();
  const text = String(title || "").trim();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const half = Math.min(140, vw / 2 - 12);
    let left = r.left + r.width / 2;
    left = Math.max(half + 12, Math.min(vw - half - 12, left));
    const top = down ? r.bottom + 8 : r.top - 8;
    setPos({ top, left });
  }, [down]);

  const show = useCallback(() => {
    reposition();
    setOpen(true);
  }, [reposition]);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  if (!text) return null;

  const isTouch =
    typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches;

  const bubble =
    open &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        className={`inv-tip-bubble${dark ? " inv-tip-bubble--dark" : ""}`}
        role="tooltip"
        style={{
          top: pos.top,
          left: pos.left,
          transform: down ? "translate(-50%, 0)" : "translate(-50%, -100%)",
        }}
      >
        {text}
      </div>,
      document.body
    );

  return (
    <>
      <span
        ref={ref}
        className={`inv-tip-mark${isTouch && open ? " inv-tip-open" : ""}`}
        tabIndex={0}
        role="button"
        aria-label={text}
        onMouseEnter={isTouch ? undefined : show}
        onMouseLeave={isTouch ? undefined : hide}
        onFocus={isTouch ? undefined : show}
        onBlur={isTouch ? hide : undefined}
        onClick={(e) => {
          if (!isTouch) return;
          e.stopPropagation();
          if (open) hide();
          else show();
        }}
      >
        <span className="inv-tip-trigger" aria-hidden="true">
          ?
        </span>
      </span>
      {bubble}
    </>
  );
}

export { useInvelpsTooltipStyles };
